<template>
  <BaseModule
    v-bind="$props"
    class="container experience-list"
    data-module="experience-list"
    :style="{
      '--img-width': imgWidth + 'px',
      '--img-height': imgHeightByRatio + '%',
      '--tag-bg-color': tagBackgroundColor,
      '--selected-tag-color': selectedTagColor,
      '--tag-text-color': tagTextColor,
      '--card-bg-color': cardBackgroundColor,
      '--card-text-color': cardTextColor,
      '--see-more-button-color': seeMoreButtonColor,
    }"
  >
    <Warning
      v-if="editing"
      text="O módulo não possui um hotel selecionado"
    >
    </Warning>

    <Loader v-else-if="loading" />

    <div
      v-else
      class="content"
    >
      <header
        v-if="title || text"
        class="header base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
      </header>

      <ul
        v-if="!isMobile && availableTags.length > 1"
        class="tag-filter-list"
      >
        <li
          v-for="tag in availableTags"
          :key="tag.index"
          class="tag-filter-list__item"
        >
          <button
            :class="{ selected: selectedTag === tag }"
            @click="setFilter(tag)"
          >
            {{ tag !== '$all' ? tag : $t('all') }}
          </button>
        </li>
      </ul>

      <div
        v-if="isMobile && availableTags.length > 1"
        class="mobile-tag-filter-container"
      >
        <div
          class="mobile-tag-filter"
          :class="{ open: mobileTagsOpen }"
        >
          <button
            class="mobile-tag-filter__call"
            @click="toggleFilterDrop()"
          >
            {{ $t('filterBy') }}
            <IconFilter :color="tagTextColor" />
          </button>
          <ul class="mobile-tag-filter-list">
            <li
              v-for="tag in availableTags"
              :key="tag.index"
              class="mobile-tag-filter-list__item"
            >
              <button
                :class="{ selected: selectedTag === tag }"
                @click="setFilter(tag)"
              >
                {{ tag !== '$all' ? tag : $t('all') }}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div class="list-wrapper">
        <ul
          v-if="showExperiences"
          class="exp-list"
        >
          <li
            v-for="exp in visibleExperienceList"
            :key="exp.code"
            :data-experience="exp.code"
            class="exp-list__item"
          >
            <div class="experience-card">
              <div class="aspect-ratio-parent">
                <div class="aspect-ratio-child">
                  <LImage
                    v-if="exp.image"
                    class="absolute-full"
                    :src="exp.image"
                    :alt="exp.name"
                  />
                </div>
              </div>
              <p
                v-if="exp.discount"
                class="promo"
              >
                {{ $tc('discount', exp.discount) }}
              </p>

              <div class="infos">
                <h4 class="name">
                  {{ exp.name }}
                </h4>
                <div
                  class="exp-description"
                  :class="{
                    'exp-description--empty': !hasText(exp.description),
                    clamped: exp.seeMoreEnabled,
                    'see-more': exp.seeMore,
                  }"
                >
                  <p
                    :ref="'description-text-' + exp.code"
                    class="exp-description__text"
                  >
                    {{ exp.description }}
                  </p>
                </div>

                <button
                  class="toggle-see-more"
                  :class="{
                    'toggle-see-more--hidden': !exp.seeMoreEnabled,
                    open: exp.seeMore,
                  }"
                  type="button"
                  @click="toggleSeeMore(exp)"
                >
                  <span
                    v-if="!exp.seeMore"
                    class="more"
                    >{{ $t('readMore') }}</span
                  >
                  <span
                    v-else
                    class="less"
                    >{{ $t('readLess') }}</span
                  >
                </button>
                <div
                  v-if="exp.priceWithDiscount && showValor"
                  class="value"
                >
                  <div
                    v-if="exp.discount"
                    class="value__label small-info"
                  >
                    <span class="slashed">
                      {{ valueFormat(exp.priceFull) }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="small-info"
                  >
                    {{ $t('priceText') }}
                  </div>
                  <div
                    class="value__info"
                    :style="{ color: getValueColor(exp) }"
                  >
                    <span v-html="getValueHTML(exp.priceWithDiscount)"></span>
                    <span class="small-info"> /{{ unitType(exp) }}</span>
                  </div>
                </div>

                <EditorLink
                  :model="exp.linkUrl"
                  class="cta"
                >
                  {{ buttonText }}
                </EditorLink>
              </div>
            </div>

            <div
              v-if="exp.policy"
              class="disclaimer"
              @mouseover="showTooltip"
              @mouseleave="hideTooltip"
            >
              <span class="slb-tooltip arrow-right-down">
                {{ exp.policy }}
              </span>
              <IconInfoTooltip />
            </div>
          </li>
        </ul>

        <EmptyState
          v-if="isEmptyState"
          :title="$t('notAvailableTitle')"
          :subtitle="$t('notAvailableSubtitle')"
          :img="experiencesImg"
        />
      </div>
      <button
        v-show="hasExperiencesToLoad"
        class="cta vazado cta--ver-mais"
        @click="showMoreExperiences()"
      >
        {{ $t('seeMore') }}
      </button>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import base from '@/mixins/base';
import getApiHotels from '@/mixins/getApiHotels';
import formatCurrency from '@/helpers/formatCurrency';
import Loader from '@/components/Loader.vue';
import Warning from '@/components/Warning.vue';
import IconInfoTooltip from '@/components/icons/IconInfoTooltip.vue';
import IconFilter from '@/components/icons/IconFilter.vue';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';
import EmptyState from './EmptyState.vue';

const emptyExperiencesUrlPath = require('@/assets/empty-accommodations.png');

export default {
  name: 'ExperienceList',
  components: {
    BaseModule,
    Loader,
    Warning,
    EditorLink,
    EmptyState,
    IconInfoTooltip,
    IconFilter,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    hotel: {
      type: Number,
      default: null,
    },
    customExperiences: {
      type: Array,
      default() {
        return [];
      },
    },
    categories: {
      type: Array,
      default() {
        return [];
      },
    },
    tagBackgroundColor: {
      type: String,
      default: '#ffffff',
    },
    selectedTagColor: {
      type: String,
      default: '#dedede',
    },
    tagTextColor: {
      type: String,
      default: '#575757',
    },
    showValor: {
      type: Boolean,
      default: true,
    },
    orderBy: {
      type: String,
      default: 'lowest-price',
    },
    buttonText: {
      type: String,
      default: 'Saiba Mais',
    },
    cardBackgroundColor: {
      type: String,
      default: '#fbfbfb',
    },
    cardTextColor: {
      type: String,
      default: '#474747',
    },
    seeMoreButtonColor: {
      type: String,
      default: '#575757',
    },
    imgWidth: {
      type: Number,
      default: 280,
    },
    imgHeight: {
      type: Number,
      default: 450,
    },
  },
  data() {
    return {
      loading: false,
      experiences: [],
      cleanExperiences: [],
      cardsPerRow: 3,
      visibleExperiencesNumber: 6,
      selectedTag: '$all',
      mobileTagsOpen: false,
      experiencesImg: emptyExperiencesUrlPath,
      isMobile: window.innerWidth < 640,
    };
  },
  computed: {
    availableTags() {
      const tags = ['$all'];
      const usedTags = [];

      this.cleanExperiences.forEach((exp) => {
        exp.tags.forEach((expTag) => {
          if (!usedTags.includes(expTag)) {
            usedTags.push(expTag);
          }
        });
      });

      tags.push(...this.categories.filter((tag) => usedTags.includes(tag)));
      return tags;
    },
    hasExperiences() {
      return this.cleanExperiences.length > 0;
    },
    selectedTagExperiences() {
      if (this.hasExperiences) {
        const list = this.orderList(this.cleanExperiences);

        if (this.selectedTag === '$all') {
          return list;
        }
        return list.filter((item) => item.tags.includes(this.selectedTag));
      }
      return [];
    },
    visibleExperienceList() {
      return this.selectedTagExperiences.slice(
        0,
        this.visibleExperiencesNumber,
      );
    },
    showExperiences() {
      return !this.loading && this.hasExperiences;
    },
    hasExperiencesToLoad() {
      return this.visibleExperiencesNumber < this.selectedTagExperiences.length;
    },
    isEmptyState() {
      return !this.hasExperiences && !this.loading && !this.editing;
    },
    editing() {
      return typeof this.$parent === 'undefined' && !this.hotel;
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    imgHeightByRatio() {
      // altura da imagem é calculada através de padding-top, assim as proporções da imagem é respeitadas quando a largura não cabe na janela
      return Number(((100 / this.imgWidth) * this.imgHeight).toFixed(2));
    },
    ...mapState(['apiData']),
    ...mapGetters(['settings']),
  },
  watch: {
    hotel(val) {
      if (typeof val === 'number') {
        this.requestExperiences();
        this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
          path: 'data.customExperiences',
          value: [],
        });
      }
    },
    visibleExperienceList() {
      this.manageSeeMore();
    },
    categories() {
      this.customExperiences.forEach((exp) => {
        const usable = exp.tags.filter((tag) => this.categories.includes(tag));
        // eslint-disable-next-line no-param-reassign
        exp.tags = usable;
      });
    },
    customExperiences: {
      handler() {
        this.setCleanExperiences();
      },
      deep: true,
    },
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 640;
    });
    this.getApiHotels();

    if (this.hotel) {
      this.requestExperiences();
    }

    this.cardsPerRow = this.rowCardCalc(window.innerWidth);
    this.visibleExperiencesNumber = this.cardsPerRow * 2;
  },
  methods: {
    getValueColor(exp) {
      return exp.discount
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    async requestExperiences() {
      this.loading = true;
      const urlExperiences = `${this.settings.letsbookApiId}/experiencia/${this.hotel}/${this.apiLocale}`;

      if (!this.apiData[urlExperiences]) {
        try {
          await this.$store.dispatch('loadApiData', urlExperiences);
        } catch {
          return;
        }
      }

      this.experiences = this.apiData[urlExperiences];
      this.loading = false;
      this.setCleanExperiences();
    },
    valueFormat(price) {
      return formatCurrency(price, 'BRL');
    },
    getValueHTML(value) {
      const formattedValue = this.valueFormat(value);
      const rgx = /(\D*)(\d{1,3}(?:[.,\s]\d{3})*)((?:[.,\s]\d{2})?\D*)/; // (any)(INT)(any)
      return formattedValue.replace(rgx, '$1<span class="integer">$2</span>$3'); // $1 might be empty
    },
    unitType(exp) {
      return exp.categoryName === 'Avulso'
        ? this.$t('unit')
        : this.$t('person');
    },
    hasText(text) {
      return typeof text === 'string' && /\S/.test(text);
    },
    setFilter(expTag) {
      this.selectedTag = expTag;
      this.mobileTagsOpen = false;
    },
    toggleFilterDrop() {
      this.mobileTagsOpen = !this.mobileTagsOpen;
    },
    manageSeeMore() {
      // precisa do nextTick ou ele não encontra os elementos na primeira inicialização
      this.$nextTick(() => {
        this.cleanExperiences.forEach((exp) => {
          const el = this.$refs[`description-text-${exp.code}`];

          if (el && el[0]?.offsetHeight > 17 * 3) {
            // eslint-disable-next-line no-param-reassign
            exp.seeMoreEnabled = true;
          }
        });
      });
    },
    toggleSeeMore(exp) {
      // eslint-disable-next-line no-param-reassign
      exp.seeMore = !exp.seeMore;
    },
    showTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      const position = el.getBoundingClientRect();
      if (position.top < slbTooltip.clientHeight + 10) {
        slbTooltip.classList.add('open-down');
      } else {
        slbTooltip.classList.add('open-up');
      }
    },
    hideTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      slbTooltip.classList.remove('open-up', 'open-down');
    },
    getLinkLetsbook(linkHash) {
      return this.settings.letsbookUrl.replace(
        /\/admin\/?$/i,
        `/experiencias/linkpublico?r=${linkHash}`,
      );
    },
    expLinkModel(exp) {
      const hotelLink = this.getLinkLetsbook(exp.LinkPublico);
      return {
        openInNewTab: true,
        type: 'externalLink',
        value: hotelLink,
      };
    },
    rowCardCalc(innerWidth) {
      const containerWidth = Math.min(1200, innerWidth - 2 * 20);
      const flexGap = 24;
      const cardsPerRow = Math.floor(
        (containerWidth + flexGap) / (this.imgWidth + flexGap),
      );
      return Math.max(1, cardsPerRow);
    },
    showMoreExperiences() {
      this.visibleExperiencesNumber += this.cardsPerRow * 2;
    },
    setCleanExperiences() {
      const newList = this.experiences.map((exp) => {
        // procura no customExperiences pelo id para usar as informações customizadas se disponíveis
        const customData = this.customExperiences.find(
          (custom) => custom.experienceId === exp.Codigo,
        );

        return {
          code: exp.Codigo,
          name: exp.Nome,
          image: customData?.image || exp.UrlImagem,
          description: exp.Descricao,
          policy: exp.PoliticaResumida,
          priceFull: exp.MenorPrecoSemDesconto,
          priceWithDiscount: exp.MenorPreco,
          discount: exp.PercentualDesconto,
          categoryName: exp.CategoriaNome,
          seeMoreEnabled: false,
          seeMore: false,
          tags: customData?.tags || [],
          linkUrl:
            customData?.linkUrl.type !== 'defaultLink' &&
            customData?.linkUrl.value
              ? customData.linkUrl
              : this.expLinkModel(exp),
        };
      });
      this.cleanExperiences = newList;
    },
    orderList(experiences) {
      const expList = [...experiences];

      switch (this.orderBy) {
        case 'lowest-price':
          return expList.sort(
            (expA, expB) => expA.priceWithDiscount - expB.priceWithDiscount,
          );
        case 'highest-price':
          return expList.sort(
            (expA, expB) => expB.priceWithDiscount - expA.priceWithDiscount,
          );
        case 'highest-discount':
          return expList.sort((expA, expB) => {
            const discount1 = expA.discount ? expA.discount : 0;
            const discount2 = expB.discount ? expB.discount : 0;

            return discount2 - discount1;
          });
        default:
          return expList;
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'config',
            position: 0,
            label: 'Configurações',
            fields: [
              {
                name: 'hotel',
                type: 'VueSelect',
                label: 'Listar experiências',
                helpText:
                  'Selecione um hotel para listar as experiências<br><span class="warning">Atenção: você perderá seus "itens editados" se trocar o hotel</span>',
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'showValor',
                type: 'Checkbox',
                label: 'Mostrar preços',
              },
              {
                name: 'orderBy',
                type: 'VueSelect',
                label: 'Ordenar experiências por:',
                options: [
                  {
                    label: 'Menor preço',
                    value: 'lowest-price',
                  },
                  {
                    label: 'Maior preço',
                    value: 'highest-price',
                  },
                  {
                    label: 'Maior desconto',
                    value: 'highest-discount',
                  },
                ],
              },
            ],
          },
          {
            name: 'text',
            fields: [
              {
                name: 'title',
                maxlength: 40,
              },
              {
                name: 'text',
                type: 'Html',
                label: 'Texto',
              },
              {
                name: 'buttonText',
                type: 'ShortText',
                maxlength: 24,
                label: 'Texto dos botões',
              },
            ],
            toRemove: ['subtitle'],
          },
          {
            name: 'categories',
            label: 'Categorias',
            helpText:
              'Crie categorias para separação e filtragem dos itens, em "Editar itens" você adiciona as categorias criadas aqui',
            fields: [
              {
                name: 'categories',
                type: 'Tags',
                label: 'Lista de categorias disponíveis',
              },
            ],
          },
          {
            name: 'customExperiences',
            label: 'Editar itens',
            helpText:
              'Por padrão, usamos as informações registradas no Motor de Reservas, mas aqui você pode editar as imagens e links de cada item de experiência.',
            fields: [
              {
                name: 'customExperiences',
                type: 'List',
                itemLabel({ experienceId }) {
                  if (!experienceId) {
                    return '<span style="color:red">Experiência não selecionada</span>';
                  }

                  if (this.loading) {
                    return `Experiência ID ${experienceId}`;
                  }

                  const thisExp = this.experiences.find(
                    (exp) => exp.Codigo === experienceId,
                  );

                  return thisExp
                    ? thisExp.Nome
                    : '<span style="color:red">Experiência selecionada não encontrada</span>';
                },
                addItemLabel: 'Editar item de experiência',
                isSortable: false,
                fields: [
                  {
                    name: 'experienceId',
                    type: 'VueSelect',
                    label: 'Selecionar experiência',
                    options() {
                      const expToOptions = (experiences) =>
                        experiences.map((exp) => ({
                          label: exp.Nome,
                          value: exp.Codigo,
                        }));

                      // Are experiences already loaded?
                      if (this.experiences.length > 0) {
                        return expToOptions(this.experiences);
                      }

                      // THIS IS PURE MAGIC
                      return new Promise((resolve) => {
                        const unwatch = this.$watch(
                          'experiences',
                          (experiences) => {
                            resolve(expToOptions(experiences));
                            unwatch();
                          },
                        );
                      });
                    },
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem',
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                    availableTypes: ['externalLink', 'page', 'defaultLink'],
                    default: {
                      type: 'defaultLink',
                      value: '',
                    },
                  },
                  {
                    name: 'tags',
                    type: 'VueSelect',
                    label: 'Categorias deste item',
                    options: {
                      watch: true,
                      attribute: 'categories',
                      handler(categories) {
                        return categories;
                      },
                    },
                    libOptions: {
                      multiple: true,
                      closeOnSelect: false,
                      selectOnTab: true,
                      placeholder: 'Selecione ou digite as categorias',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            label: 'Cores',
            fields: [
              {
                name: 'cardBackgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo dos itens',
              },
              {
                name: 'cardTextColor',
                type: 'ColorPicker',
                label: 'Cor do texto dos itens',
              },
              {
                name: 'seeMoreButtonColor',
                type: 'ColorPicker',
                label: 'Cor do botão "Ver mais experiências"',
              },
              {
                name: 'tagBackgroundColor',
                type: 'ColorPicker',
                label: 'Cor dos filtros de categoria',
              },
              {
                name: 'selectedTagColor',
                type: 'ColorPicker',
                label: 'Cor da categoria ativa',
              },
              {
                name: 'tagTextColor',
                type: 'ColorPicker',
                label: 'Cor do texto dos filtros de categoria',
              },
            ],
          },
          {
            name: 'imageConfig',
            position: 0,
            label: 'Dimensões das imagens',
            fields: [
              {
                name: 'imgWidth',
                label: 'Largura das imagens',
                type: 'Range',
                min: 240,
                max: 600,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'imgHeight',
                label: 'Altura das imagens',
                type: 'Range',
                min: 240,
                max: 600,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        discount: '{count}% OFF',
        priceText: 'a partir de',
        unit: 'unidade',
        person: 'pessoa',
        seeMore: 'Ver mais experiências',
        readMore: 'Ler mais',
        readLess: 'Recolher',
        filterBy: 'Filtrar por:',
        all: 'Todos',
        notAvailableTitle:
          'Ops, não encontramos nenhuma experiência disponível.',
        notAvailableSubtitle:
          'Em breve listaremos aqui todas as nossas experiências para você escolher a que mais se encaixa no seu perfil.',
      },
      en: {
        discount: '{count}% OFF',
        priceText: 'prices from',
        unit: 'unit',
        person: 'person',
        seeMore: 'See more experiences',
        readMore: 'Read more',
        readLess: 'Less',
        filterBy: 'Filter by:',
        all: 'All',
        notAvailableTitle: 'Ops, we did not find any available item.',
        notAvailableSubtitle:
          "We'll soon list all our experiencies here so that you can find the one that best suits your profile.",
      },
      es: {
        discount: '{count}% OFF',
        priceText: 'a partir de',
        unit: 'unidad',
        person: 'persona',
        seeMore: 'Ver más experiencias',
        readMore: 'Ler más',
        readLess: 'Menos',
        filterBy: 'Filtrar por:',
        all: 'Todos',
        notAvailableTitle: 'Ops, no encontramos experiencias disponibles.',
        notAvailableSubtitle:
          'En breve enumeraremos aquí todos nuestros experiencias para que elijas el que mejor se adapta a tu perfil.',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/base/mixins.scss';

.container {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(sm) {
    padding-left: 8vw;
    padding-right: 8vw;
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}

.content {
  max-width: 1200px !important;
}

.experience-list {
  text-align: center;
  max-width: 100%;

  .tag-filter-list {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin-bottom: 32px;
    padding: 0;
    justify-content: center;
    gap: 16px;

    &__item {
      width: 100%;
      max-width: 220px;

      button {
        cursor: pointer;
        font-size: 14px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0em;
        border: 1px solid;
        border-color: var(--tag-text-color, $gray-900);
        border-radius: 8px;
        width: 100%;
        padding: 8px;
        background-color: var(--tag-bg-color, $white);
        color: var(--tag-text-color, $gray-900);

        &.selected {
          border: 1px solid rgba(0, 0, 0, 0);
          background-color: var(--selected-tag-color, $gray-200);
        }
      }
    }
  }

  .mobile-tag-filter-container {
    width: 100%;
    position: relative;
    height: 56px;
    margin-bottom: 32px;

    .mobile-tag-filter {
      background-color: var(--tag-bg-color, $white);
      z-index: 1;
      border: 1px solid;
      border-color: var(--tag-text-color, $gray-900);
      border-radius: 8px;
      width: 100%;
      padding: 0 24px 24px;
      position: absolute;
      top: 0;
      left: 0;
      max-height: 56px;
      overflow: hidden;
      transition: max-height 0.5s ease;

      &.open {
        max-height: 2000px;
      }

      &__call {
        font-weight: 600;
        font-size: 16px;
        color: var(--tag-text-color, $gray-900);
        width: 100%;
        display: flex;
        padding: 16px 8px;
        margin-bottom: 4px;
        justify-content: space-between;
        outline: none;
      }

      .mobile-tag-filter-list {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        width: 100%;
        gap: 10px;

        &__item {
          width: 100%;

          button {
            cursor: pointer;
            font-family: Raleway;
            font-size: 14px;
            font-weight: 400;
            line-height: 16px;
            letter-spacing: 0em;
            border: 1px solid;
            border-color: var(--tag-text-color, $gray-900);
            border-radius: 8px;
            width: 100%;
            padding: 8px;
            background-color: var(--tag-bg-color, $white);
            color: var(--tag-text-color, $gray-900);

            &.selected {
              font-weight: 800;
              border: 1px solid rgba(0, 0, 0, 0);
              background-color: var(--selected-tag-color, $gray-200);
            }
          }
        }
      }
    }
  }

  .list-wrapper {
    display: flex;
    align-items: flex-start;

    .exp-list {
      display: flex;
      flex-direction: column;
      gap: 32px;
      width: 100%;

      @include media-breakpoint-up(sm) {
        flex-direction: row;
        flex-wrap: wrap;
        flex-grow: 1;
        justify-content: center;
        gap: 24px;
      }

      &__item {
        position: relative;
        max-width: 100%;

        .disclaimer {
          text-align: right;
          position: absolute;
          top: 0;
          right: 0;
          padding: 10px;
          cursor: pointer;

          .slb-tooltip {
            visibility: hidden;
            opacity: 0;
            color: $gray-70;
            left: unset;
            right: 0;
            z-index: 99;

            &.open-up,
            &.open-down {
              opacity: 1;
              visibility: visible;
              transform: translateY(0);
            }
          }
        }

        .experience-card {
          display: flex;
          flex-direction: column;
          background-color: var(--card-bg-color, $white);
          color: var(--card-text-color, $gray-900);
          width: var(--img-width);
          max-width: 100%;
          height: auto;
          position: relative;
          border-radius: 12px;
          overflow: hidden;

          @include media-breakpoint-down(sm) {
            width: 100%;
          }

          .aspect-ratio-parent {
            width: 100%;

            .aspect-ratio-child {
              width: 100%;
              padding-top: var(--img-height);
              position: relative;
            }

            .img {
              border-radius: 12px;
            }
          }

          .infos {
            padding: 24px;
            text-align: left;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }

          .promo {
            font-size: $size-normal;
            font-weight: $lb-bold;
            line-height: 2.2;
            padding: 0 12px;
            text-transform: uppercase;
            position: absolute;
            top: 0;
            left: 0;
            color: $white;
            background-color: var(--promo-color);
          }

          .name {
            margin: 0;
            font-size: $size-normal;
            font-weight: $lb-bold;
            height: 2.4em;
            line-height: 1.2;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .exp-description {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            font-size: $size-small;
            text-align: left;
            margin-top: 18px;
            height: calc(3 * 17px);
            max-height: calc(3 * 17px);
            transition: max-height 1s ease-in-out;

            &.clamped {
              -webkit-line-clamp: 3;
            }

            &.see-more {
              -webkit-line-clamp: unset !important;
              height: auto;
              max-height: 1000px;
            }

            &__text {
              line-height: 17px;
            }

            &--empty {
              height: calc(3 * 21px);
            }
          }

          .toggle-see-more {
            position: relative;
            border: 0;
            background: none;
            font-weight: $lb-bold;
            outline: inherit;
            padding: 4px 0;
            margin-bottom: 8px;
            cursor: pointer;
            color: var(--primary-color);

            &--hidden {
              visibility: hidden;
              opacity: 0;
            }

            &::after {
              content: '';
              margin-left: 0.625vw;
              display: inline-block;
              border-top: 7px solid;
              border-left: 5px solid transparent !important;
              border-right: 5px solid transparent !important;
              position: absolute;
              top: 50%;
              transform: translateY(-50%) rotate(0);
              transition: transform 0.4s ease-in-out;
            }

            &.open {
              &::after {
                transform: translateY(-50%) rotate(180deg);
              }
            }
          }

          .value {
            margin-bottom: 16px;

            &__label {
              .slashed {
                text-decoration: line-through;
                text-transform: uppercase;
              }
            }

            &__info {
              font-weight: $lb-bold;
              font-size: $size-smaller;

              ::v-deep .integer {
                line-height: 1;
                font-size: 20px;
              }
            }
          }

          .small-info {
            font-size: $size-smaller;
            font-weight: 400;
          }

          .cta {
            width: auto;
            min-width: 150px;
            max-width: 100%;
            padding: 16px 8px;
            font-weight: $lb-bold;
            white-space: normal;
            text-decoration: none;
            overflow-wrap: anywhere;
            border-radius: 12px;
            transition: 0.2s;
          }
        }
      }
    }
  }

  .cta {
    &.cta--ver-mais {
      color: var(--see-more-button-color);
      border-color: var(--see-more-button-color);
      margin: 32px auto 0;
      border-radius: 12px;
      overflow: hidden;
    }
  }
}
</style>
